import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Search, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Calendar,
  User,
  MapPin,
  Tag,
  ChevronRight,
  RotateCcw
} from 'lucide-react';
import { STATUTS_PLAINTE } from '../hooks/usePlaintes';
import { usePlainteSearch } from '../hooks/usePlainteSearch';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const SearchCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-lg);
  margin-top: -2rem;
  position: relative;
  z-index: 2;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 1rem 1.5rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1.1rem;
  font-family: monospace;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }

  &::placeholder {
    color: var(--text-light);
    text-transform: none;
    font-family: var(--font-primary);
    letter-spacing: normal;
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 38, 100, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const HelpText = styled.p`
  text-align: center;
  color: var(--text-light);
  font-size: 0.9rem;
`;

const ResultCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  margin-top: 2rem;
  overflow: hidden;
`;

const ResultHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  .reference {
    font-family: monospace;
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: 2px;
  }

  .date {
    opacity: 0.9;
    font-size: 0.9rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: 0.9rem;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`;

const ResultBody = styled.div`
  padding: 2rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  svg {
    color: var(--primary-blue);
    flex-shrink: 0;
    margin-top: 0.2rem;
  }

  .label {
    font-size: 0.85rem;
    color: var(--text-light);
    margin-bottom: 0.25rem;
  }

  .value {
    font-weight: 600;
    color: var(--text-dark);
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.1rem;
    color: var(--text-dark);
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      color: var(--primary-blue);
    }
  }
`;

const ContentBox = styled.div`
  background: var(--background-light);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  line-height: 1.7;
  color: var(--text-medium);
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 2rem;

  &::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: var(--border-light);
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 1.5rem;

  &::before {
    content: '';
    position: absolute;
    left: -1.5rem;
    top: 0.25rem;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.$color || 'var(--primary-blue)'};
    border: 3px solid var(--white);
    box-shadow: 0 0 0 2px ${props => props.$color || 'var(--primary-blue)'};
  }

  .date {
    font-size: 0.85rem;
    color: var(--text-light);
    margin-bottom: 0.25rem;
  }

  .title {
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.25rem;
  }

  .comment {
    font-size: 0.9rem;
    color: var(--text-medium);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-light);

  svg {
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.25rem;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: #FEE2E2;
  border-radius: var(--radius-lg);
  margin-top: 2rem;
  color: #DC2626;

  svg {
    margin-bottom: 1rem;
  }
`;

const ReponseBox = styled.div`
  background: #D1FAE5;
  border-left: 4px solid #059669;
  border-radius: var(--radius-md);
  padding: 1.5rem;
  margin-top: 1rem;

  .header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #065F46;
    margin-bottom: 0.75rem;
  }

  .content {
    color: #065F46;
    line-height: 1.7;
  }

  .date {
    font-size: 0.85rem;
    color: #6B7280;
    margin-top: 0.75rem;
  }
`;

const SuiviPlainte = () => {
  const [searchRef, setSearchRef] = useState('');
  const { plainte, loading, error, searchPlainte, reset } = usePlainteSearch();

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchRef.trim();
    console.log('Recherche de la référence:', trimmed);
    searchPlainte(trimmed);
  };

  const handleReset = () => {
    setSearchRef('');
    reset();
  };

  return (
    (<PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Suivi de Réclamation</HeroTitle>
          <HeroSubtitle>
            Consultez l'état d'avancement de votre dossier en temps réel
          </HeroSubtitle>
        </HeroContent>
      </Hero>
      <Container>
        <SearchCard>
          <SearchForm onSubmit={handleSubmit}>
            <SearchInput
              type="text"
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              placeholder="Entrez votre numéro de référence (ex: PLT-2502-ABCD)"
              maxLength={20}
            />
            <SearchButton type="submit" disabled={loading || !searchRef.trim()}>
              {loading ? (
                'Recherche...'
              ) : (
                <>
                  <Search size={20} />
                  Rechercher
                </>
              )}
            </SearchButton>
          </SearchForm>
          
          <HelpText>
                      Entrez le numéro de référence qui vous a été fourni lors du dépôt de votre Réclamation.
                    </HelpText>
        </SearchCard>

        {error && (
          <ErrorState>
            <AlertCircle size={48} />
            <h3>Réclamation non trouvée</h3>
            <p>{error}</p>
            <p style={{ fontSize: '0.85rem', marginTop: '1rem' }}>
              Référence recherchée : <strong>{searchRef.toUpperCase()}</strong>
            </p>
            <button 
              onClick={handleReset}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                background: '#DC2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Réessayer
            </button>
          </ErrorState>
        )}

        {plainte && (
          <ResultCard>
            <ResultHeader>
              <div>
                <div className="reference">{plainte.reference}</div>
                <div className="date">
                  <Calendar size={14} style={{ display: 'inline', marginRight: '0.3rem' }} />
                  Déposée le {plainte.date.toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </div>
              </div>
              <StatusBadge 
                $bg={STATUTS_PLAINTE[plainte.statut].bg} 
                $color={STATUTS_PLAINTE[plainte.statut].color}
              >
                {plainte.statut === 'nouvelle' && <AlertCircle size={16} />}
                {plainte.statut === 'en_cours' && <Clock size={16} />}
                {plainte.statut === 'traitee' && <CheckCircle size={16} />}
                {plainte.statut === 'classee' && <XCircle size={16} />}
                {STATUTS_PLAINTE[plainte.statut].label || plainte.statut}
              </StatusBadge>
            </ResultHeader>

            <ResultBody>
              <InfoGrid>
                <InfoItem>
                  <User size={20} />
                  <div>
                    <div className="label">Déposée par</div>
                    <div className="value">{plainte.prenom} {plainte.nom}</div>
                  </div>
                </InfoItem>

                <InfoItem>
                  <MapPin size={20} />
                  <div>
                    <div className="label">Localité</div>
                    <div className="value">{plainte.localite}</div>
                  </div>
                </InfoItem>

                <InfoItem>
                  <Tag size={20} />
                  <div>
                    <div className="label">Type de Réclamation</div>
                    <div className="value">{plainte.type}</div>
                  </div>
                </InfoItem>

                <InfoItem>
                  <FileText size={20} />
                  <div>
                    <div className="label">Objet</div>
                    <div className="value">{plainte.objet}</div>
                  </div>
                </InfoItem>
              </InfoGrid>

              <Section>
                <h3><FileText size={20} /> Description</h3>
                <ContentBox>{plainte.description}</ContentBox>
              </Section>

              {plainte.reponse && (
                <Section>
                  <h3><CheckCircle size={20} /> Réponse de la Médiature</h3>
                  <ReponseBox>
                    <div className="header">
                      <CheckCircle size={18} />
                      Réponse officielle
                    </div>
                    <div className="content">{plainte.reponse}</div>
                    {plainte.dateReponse && (
                      <div className="date">
                        Répondue le {new Date(plainte.dateReponse.toDate ? plainte.dateReponse.toDate() : plainte.dateReponse).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </ReponseBox>
                </Section>
              )}

              <Section>
                <h3><Clock size={20} /> Historique du dossier</h3>
                <Timeline>
                  {(plainte.historique || []).map((item, index) => (
                    <TimelineItem 
                      key={index}
                      $color={STATUTS_PLAINTE[item.statut].color}
                    >
                      <div className="date">
                        {new Date(item.date).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div className="title">
                        {STATUTS_PLAINTE[item.statut].label || item.statut}
                      </div>
                      <div className="comment">{item.commentaire}</div>
                    </TimelineItem>
                  ))}
                </Timeline>
              </Section>

              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <button 
                  onClick={handleReset}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: 'var(--background-light)',
                    border: '2px solid var(--border-light)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-dark)',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <RotateCcw size={18} />
                  Nouvelle recherche
                </button>
              </div>
            </ResultBody>
          </ResultCard>
        )}

        {!plainte && !error && !loading && searchRef === '' && (
          <EmptyState>
            <Search size={64} />
            <h3>Suivez votre dossier</h3>
            <p>Entrez votre numéro de référence ci-dessus pour consulter l'état de votre Réclamation.</p>
          </EmptyState>
        )}
      </Container>
    </PageContainer>)
  );
};

export default SuiviPlainte;

